import React, { useState } from "react"
import { Button, Tag, Flex, Card } from "antd"
import { UpOutlined, DownOutlined, LinkOutlined } from "@ant-design/icons"
import styles from "./index.module.css"
import PropTypes from "prop-types"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { BookingNotFound } from "ui/Icon/DSIcon/BookingNotFound"
import { Text } from "ui/Typography"
import { useTranslation } from "react-i18next"
import { colors } from "themes/variables"

const UpcomingBookings = ({ bookings = [], totalCount = 0 }) => {
  const [expandedDates, setExpandedDates] = useState({})
    const { t } = useTranslation()

  const toggleDate = (date) => {
    setExpandedDates((prev) => ({
      ...prev,
      [date]: !prev[date]
    }))
  }

  return (
    <Flex vertical gap={24} className={styles.container}>
      {/* Header */}
      <Flex align="center" justify="space-between" className={styles.header}>
        <h2 className={styles.title}>
          Upcoming Bookings <span className={styles.count}>({totalCount})</span>
        </h2>
      </Flex>
      <WithEmptyStateWrapper
        name="bookings"
        hasItems={bookings?.length > 0}
        fallback={
          <Card className="py-5xl">
            <Flex vertical justify="center" align="center" gap={12}>
              <BookingNotFound />
              <Text color={colors.textPrimary} weight="semibold">
                {t("crewMember.bookings.emptyState.title")}
              </Text>
              <Text color={colors.textSecondaryDark}>{t("crewMember.bookings.emptyState.subtitle")}</Text>
            </Flex>
          </Card>
        }
      >
        {/* Table */}
        <Flex vertical gap={16} className={styles.tableContainer}>
          {/* Table Header */}
          <div className={styles.tableHeader}>
            <div className={styles.colExperience}>Experience Name</div>
            <div className={styles.colTime}>Time</div>
            <div className={styles.colId}>ID</div>
            <div className={styles.colClient}>Client Name</div>
            <div className={styles.colCrew}>Crew Member(s)</div>
            <div className={styles.colEquipment}>Equipment</div>
            <div className={styles.colPayment}>Payment Status</div>
          </div>

          {/* Bookings grouped by date */}
          {bookings.map((group) => {
            const isExpanded = expandedDates[group.date] !== false // Default to expanded

            return (
              <Flex key={`${group.date}-${group.bookings.length}`} vertical className={styles.dateGroup}>
                {/* Date Header */}
                <Flex align="center" justify="space-between" className={styles.dateHeader} onClick={() => toggleDate(group.date)}>
                  <span className={styles.dateText}>{group.date}</span>
                  {isExpanded ? <UpOutlined className={styles.chevron} /> : <DownOutlined className={styles.chevron} />}
                </Flex>

                {/* Bookings for this date */}
                {isExpanded && (
                  <Flex vertical className={styles.bookingsList}>
                    {group.bookings.map((booking) => (
                      <div key={`${booking.id}-${booking.time}`} className={styles.bookingRow}>
                        <Flex align="center" gap={8} className={styles.colExperience}>
                          <span className={styles.experienceLink}>{booking.experienceName}</span>
                          <LinkOutlined className={styles.externalIcon} />
                        </Flex>
                        <div className={styles.colTime}>{booking.time}</div>
                        <div className={styles.colId}>{booking.id}</div>
                        <div className={styles.colClient}>{booking.clientName}</div>
                        <div className={styles.colCrew}>
                          <span className={styles.linkText}>{booking.crewMembers}</span>
                        </div>
                        <div className={styles.colEquipment}>
                          <span className={styles.linkText}>{booking.equipment}</span>
                        </div>
                        <div className={styles.colPayment}>
                          <Tag className={booking.paymentStatus === "Paid" ? styles.paidTag : styles.outstandingTag}>
                            {booking.paymentStatus}
                          </Tag>
                        </div>
                      </div>
                    ))}
                  </Flex>
                )}
              </Flex>
            )
          })}
        </Flex>
      </WithEmptyStateWrapper>
    </Flex>
  )
}

UpcomingBookings.propTypes = {
  bookings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      date: PropTypes.string,
      status: PropTypes.string
    })
  ),
  totalCount: PropTypes.number
}

export default UpcomingBookings
